// ═══════════════════════════════════════════════════════════════════
// Ratings Posterior / Calibration Layer — v4
// ───────────────────────────────────────────────────────────────────
// The category engines in `ratingsEngine.ts` produce RAW 0-100 signal
// scores from specs, bench data, installer notes and forum consensus.
// This layer turns those raw scores into *statistically defensible*
// ratings:
//
//   1. Peer-group normalisation  — a 12" SPL sub is scored against
//      other 12" SPL subs, never against a 6.5" component set. Peer
//      groups are built from application variables ONLY (size, power
//      class, channel count, output class) — never price, never brand.
//   2. Leave-one-out empirical CDF + parent-group blending, then a
//      normal-score transform (Φ⁻¹). Removes scale artefacts and makes
//      every signal comparable in standard-deviation units.
//   3. Evidence → precision. Every observation carries a source quality
//      q and an effective sample size n_eff = n / (1 + (n-1)ρ) so that
//      "many weak copies of the same forum claim" cannot outvote one
//      independent bench measurement.
//   4. Hierarchical shrinkage toward the peer-group prior. A product
//      with thin evidence lands near its peer average with a WIDE
//      interval — it is never punished for missing data.
//   5. Correlated-signal covariance (shrunk toward its diagonal) so the
//      composite interval isn't falsely narrow when signals overlap.
//   6. Anchored linear display mapping (peer median → 70, +3.09σ → 97)
//      instead of a sigmoid/CDF squash, so elite products don't get
//      compressed against the ceiling.
//   7. Price is stripped out of quality entirely and published as a
//      separate cross-fitted value residual.
//   8. Head-to-head confidence P(A > B) computed analytically from the
//      two posteriors — we never assert a winner on point estimates.
//
// Everything here is deterministic and pure: same catalog in, same
// numbers out, no randomness, no network.
// ═══════════════════════════════════════════════════════════════════

import { allProducts, type Product, type ProductCategory } from "@/data/productDatabase";
import { computeAdvancedRating, type AnyAdvancedRating } from "@/lib/ratingsEngine";
import { getBenchTest } from "@/data/benchTestData";

export const RATINGS_MODEL_VERSION = "v4.0-posterior";

// ─── Constants (versioned with the model) ──────────────────────────
/** Parent-CDF blending weight for sparse peer groups. */
const KAPPA = 30;
/** Correlation between owner/forum observations of the same product. */
const REVIEW_RHO = 0.35;
/** Residual correlation assumed between the eight engine signals. */
const SIGNAL_RHO = 0.35;
/** Covariance shrinkage toward the diagonal. */
const COV_SHRINK = 0.35;
/** Irreducible model error, in prior-SD units. */
const MODEL_SIGMA = 0.22;
/** Display anchors: peer median = 70 points, +3.09σ (99.9th pct) = 97. */
const DISPLAY_CENTER = 70;
const DISPLAY_SLOPE = (97 - 70) / 3.0902;
/** Half-width used for the "score is accurate within ±δ" readout. */
const DELTA_POINTS = 3;

// ─── Small numeric helpers ─────────────────────────────────────────
const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Abramowitz–Stegun 7.1.26 error function → standard normal CDF. */
export function normCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  const p =
    d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return z >= 0 ? 1 - p : p;
}

/** Acklam's rational approximation of the inverse standard normal CDF. */
export function normInv(p: number): number {
  const q = clamp(p, 1e-9, 1 - 1e-9);
  const a = [-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1, 2.506628277459239];
  const b = [-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1];
  const c = [-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const pLow = 0.02425;
  if (q < pLow) {
    const u = Math.sqrt(-2 * Math.log(q));
    return (((((c[0] * u + c[1]) * u + c[2]) * u + c[3]) * u + c[4]) * u + c[5]) /
      ((((d[0] * u + d[1]) * u + d[2]) * u + d[3]) * u + 1);
  }
  if (q > 1 - pLow) {
    const u = Math.sqrt(-2 * Math.log(1 - q));
    return -(((((c[0] * u + c[1]) * u + c[2]) * u + c[3]) * u + c[4]) * u + c[5]) /
      ((((d[0] * u + d[1]) * u + d[2]) * u + d[3]) * u + 1);
  }
  const u = q - 0.5;
  const r = u * u;
  return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * u /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}

// ─── Dimensions the comparison UI shows ────────────────────────────
export type RatedDimension = "reliability" | "abuseTolerance" | "buildQuality" | "userReviews";

export const RATED_DIMENSIONS: RatedDimension[] = [
  "reliability",
  "abuseTolerance",
  "buildQuality",
  "userReviews",
];

type SignalKey = keyof AnyAdvancedRating["signals"];

/**
 * Each displayed dimension is a weighted blend of engine signals.
 * Signals overlap on purpose (thermal design feeds both reliability and
 * abuse tolerance) — the covariance step below accounts for that rather
 * than pretending they are independent.
 */
const DIMENSION_SIGNALS: Record<RatedDimension, Partial<Record<SignalKey, number>>> = {
  reliability: { reliability: 0.55, failureAnalysis: 0.28, buildQuality: 0.1, installerSignal: 0.07 },
  abuseTolerance: { abuseTolerance: 0.6, measuredPerformance: 0.2, failureAnalysis: 0.13, installerSignal: 0.07 },
  buildQuality: { buildQuality: 0.62, measuredPerformance: 0.18, installerSignal: 0.12, reliability: 0.08 },
  userReviews: { verifiedReviewTrust: 0.62, installerSignal: 0.2, reliability: 0.1, failureAnalysis: 0.08 },
};

/** Composite weights over the four displayed dimensions. */
const OVERALL_WEIGHTS: Record<RatedDimension, number> = {
  reliability: 0.31,
  abuseTolerance: 0.26,
  buildQuality: 0.24,
  userReviews: 0.19,
};

// ─── Peer grouping (application variables only — no price, no brand) ─
function num(spec: string | number | undefined): number | null {
  if (spec === undefined) return null;
  const m = String(spec).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : null;
}

export function peerGroupKey(p: Product): string {
  const specs = p.specs as Record<string, string | undefined>;
  switch (p.category) {
    case "subwoofer": {
      const size = num(specs["Size"]);
      const rms = num(specs["RMS Power"]) ?? 0;
      const sizeBucket = size === null ? "unk" : size <= 8 ? "8" : size <= 10 ? "10" : size <= 12 ? "12" : size <= 15 ? "15" : "18+";
      const powerBucket = rms >= 2000 ? "spl" : rms >= 800 ? "high" : "daily";
      return `subwoofer/${sizeBucket}/${powerBucket}`;
    }
    case "amplifier": {
      const ch = num(specs["Channels"]) ?? 1;
      const rms = num(specs["RMS Power"]) ?? num(specs["RMS Power @ 1ohm"]) ?? 0;
      const chBucket = ch <= 1 ? "mono" : ch <= 2 ? "2ch" : ch <= 4 ? "4ch" : "multi";
      const powerBucket = rms >= 3000 ? "spl" : rms >= 1000 ? "high" : rms >= 400 ? "mid" : "low";
      return `amplifier/${chBucket}/${powerBucket}`;
    }
    case "speaker": {
      const size = num(specs["Size"]);
      const bucket = size === null ? "unk" : size <= 4 ? "4" : size <= 5.5 ? "5" : size <= 6.75 ? "6.5" : size <= 8 ? "8" : "large";
      const type = /component/i.test(specs["Type"] ?? "") ? "comp" : "coax";
      return `speaker/${bucket}/${type}`;
    }
    case "tweeter": {
      const rms = num(specs["RMS Power"]) ?? 0;
      return `tweeter/${rms >= 100 ? "pro" : rms >= 50 ? "high" : "sq"}`;
    }
    case "alternator": {
      const out = num(specs["Output"]) ?? num(specs["Peak Output"]) ?? 0;
      return `alternator/${out >= 350 ? "xl" : out >= 270 ? "l" : out >= 200 ? "m" : "s"}`;
    }
    default:
      return String(p.category);
  }
}

const parentGroupKey = (p: Product): ProductCategory => p.category;

// ─── Evidence → precision ──────────────────────────────────────────
export interface EvidenceProfile {
  /** Source quality in (0,1]. */
  quality: number;
  /** Effective (de-duplicated) sample size behind the observation. */
  effectiveN: number;
  /** Precision λ fed to the posterior. */
  precision: number;
  badges: string[];
}

function specCompleteness(p: Product): number {
  const required: Record<ProductCategory | string, string[]> = {
    subwoofer: ["RMS Power", "Xmax", "Fs", "Qts", "Vas", "Voice Coil", "Sensitivity"],
    amplifier: ["RMS Power", "Channels", "THD", "Efficiency", "Signal to Noise"],
    speaker: ["RMS Power", "Sensitivity", "Frequency Response", "Impedance"],
    tweeter: ["RMS Power", "Sensitivity", "Frequency Response", "Dome Material"],
    alternator: ["Output", "Idle Output", "Voltage", "Rectifier"],
  };
  const keys = required[p.category] ?? [];
  if (keys.length === 0) return 0.6;
  const specs = p.specs as Record<string, string | undefined>;
  const have = keys.filter((k) => specs[k] !== undefined && String(specs[k]).trim() !== "").length;
  return have / keys.length;
}

export function evidenceProfile(
  p: Product,
  dimension: RatedDimension,
  reviewCount = 0,
): EvidenceProfile {
  const badges: string[] = [];
  const bench = getBenchTest(p.id);
  const completeness = specCompleteness(p);

  // Base quality from how the numbers were obtained.
  let quality = 0.34 + 0.3 * completeness; // manufacturer-claimed specs only
  let effectiveN = 1 + 2 * completeness;

  if (bench) {
    quality = Math.max(quality, 0.92);
    effectiveN += 4;
    badges.push("Independent bench data");
  } else if (completeness >= 0.85) {
    badges.push("Full spec sheet");
  } else if (completeness < 0.5) {
    badges.push("Spec incomplete");
  }

  if (p.evidence?.[dimension]) {
    quality = Math.min(1, quality + 0.1);
    effectiveN += 1.5;
    badges.push("Installer / forum consensus");
  }

  if (p.confidence === "high") { quality = Math.min(1, quality + 0.08); effectiveN += 1; }
  if (p.confidence === "low") { quality *= 0.8; }

  if (dimension === "userReviews") {
    // Owner reviews are correlated: n_eff = n / (1 + (n-1)ρ) caps how much
    // signal a pile of similar reviews can contribute.
    const n = Math.max(0, reviewCount);
    const nEff = n > 0 ? n / (1 + (n - 1) * REVIEW_RHO) : 0;
    effectiveN += nEff;
    if (n >= 25) badges.push(`${n} owner reviews`);
    else if (n > 0) badges.push(`${n} reviews (thin)`);
    else badges.push("No owner reviews yet");
    quality = n >= 25 ? Math.min(1, quality + 0.12) : quality;
  }

  if (p.verification === "verified") { quality = Math.min(1, quality + 0.06); badges.push("Specs verified"); }
  if (p.verification === "needs-review") { quality *= 0.85; }

  const precision = quality * effectiveN;
  return { quality, effectiveN, precision, badges };
}

// ─── Population statistics (computed once, memoised) ───────────────
interface GroupStats {
  /** Sorted raw values per dimension, for the empirical CDF. */
  sorted: Record<RatedDimension, Float64Array>;
}

interface Population {
  groups: Map<string, GroupStats>;
  parents: Map<string, GroupStats>;
  /** θ (latent composite) per product, for percentile + value residuals. */
  theta: Map<string, number>;
  thetaByGroup: Map<string, Float64Array>;
  /** Cross-fitted log-price → θ fit per category. */
  priceFit: Map<string, { slope: number; intercept: number; residSd: number }>;
  raw: Map<string, Record<RatedDimension, number>>;
}

let POPULATION: Population | null = null;

function rawDimensions(r: AnyAdvancedRating): Record<RatedDimension, number> {
  const out = {} as Record<RatedDimension, number>;
  for (const dim of RATED_DIMENSIONS) {
    let sum = 0;
    let wsum = 0;
    for (const [sig, w] of Object.entries(DIMENSION_SIGNALS[dim])) {
      const v = r.signals[sig as SignalKey];
      if (typeof v === "number" && Number.isFinite(v)) { sum += w * v; wsum += w; }
    }
    out[dim] = wsum > 0 ? sum / wsum : 50;
  }
  return out;
}

function sortedArrays(rows: Array<Record<RatedDimension, number>>): GroupStats {
  const sorted = {} as Record<RatedDimension, Float64Array>;
  for (const dim of RATED_DIMENSIONS) {
    const a = Float64Array.from(rows.map((r) => r[dim]));
    a.sort();
    sorted[dim] = a;
  }
  return { sorted };
}

/** Leave-one-out empirical CDF against a pre-sorted array. */
function looCdf(sorted: Float64Array, x: number): number {
  const n = sorted.length;
  if (n <= 1) return 0.5;
  let lo = 0, hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (sorted[mid] < x) lo = mid + 1; else hi = mid; }
  const below = lo;
  let eq = 0;
  for (let i = below; i < n && sorted[i] === x; i++) eq++;
  // remove self (one exact match) from both the tie count and n
  const ties = Math.max(0, eq - 1);
  return (below + 0.5 * ties) / (n - 1);
}

function buildPopulation(): Population {
  const raw = new Map<string, Record<RatedDimension, number>>();
  const byGroup = new Map<string, Array<Record<RatedDimension, number>>>();
  const byParent = new Map<string, Array<Record<RatedDimension, number>>>();

  for (const p of allProducts) {
    const r = rawDimensions(computeAdvancedRating(p));
    raw.set(p.id, r);
    const g = peerGroupKey(p);
    const par = parentGroupKey(p);
    (byGroup.get(g) ?? byGroup.set(g, []).get(g)!).push(r);
    (byParent.get(par) ?? byParent.set(par, []).get(par)!).push(r);
  }

  const groups = new Map<string, GroupStats>();
  for (const [k, rows] of byGroup) groups.set(k, sortedArrays(rows));
  const parents = new Map<string, GroupStats>();
  for (const [k, rows] of byParent) parents.set(k, sortedArrays(rows));

  const pop: Population = {
    groups, parents, raw,
    theta: new Map(), thetaByGroup: new Map(), priceFit: new Map(),
  };

  // Pass 2: latent θ for every product (needs the CDFs above).
  const thetaLists = new Map<string, number[]>();
  const priceRows = new Map<string, Array<{ x: number; y: number }>>();
  for (const p of allProducts) {
    const post = posteriorCore(p, pop, 0);
    pop.theta.set(p.id, post.theta);
    const g = peerGroupKey(p);
    (thetaLists.get(g) ?? thetaLists.set(g, []).get(g)!).push(post.theta);
    if (p.price > 0) {
      const cat = String(p.category);
      (priceRows.get(cat) ?? priceRows.set(cat, []).get(cat)!).push({ x: Math.log(p.price), y: post.theta });
    }
  }
  for (const [k, list] of thetaLists) {
    const a = Float64Array.from(list);
    a.sort();
    pop.thetaByGroup.set(k, a);
  }
  // OLS of θ on log(price) per category — used only for the VALUE metric,
  // never to move the quality score.
  for (const [cat, rows] of priceRows) {
    const n = rows.length;
    if (n < 8) continue;
    const mx = rows.reduce((s, r) => s + r.x, 0) / n;
    const my = rows.reduce((s, r) => s + r.y, 0) / n;
    let sxy = 0, sxx = 0;
    for (const r of rows) { sxy += (r.x - mx) * (r.y - my); sxx += (r.x - mx) ** 2; }
    const slope = sxx > 0 ? sxy / sxx : 0;
    const intercept = my - slope * mx;
    let ss = 0;
    for (const r of rows) ss += (r.y - (intercept + slope * r.x)) ** 2;
    pop.priceFit.set(cat, { slope, intercept, residSd: Math.sqrt(ss / Math.max(1, n - 2)) || 1 });
  }
  return pop;
}

function population(): Population {
  if (!POPULATION) POPULATION = buildPopulation();
  return POPULATION;
}

/** Test / admin hook: drop the memoised population (e.g. after retuning). */
export function invalidateRatingsPopulation() {
  POPULATION = null;
}

// ─── Posterior core ────────────────────────────────────────────────
interface PosteriorCore {
  /** Posterior mean per dimension, in prior-SD units. */
  m: Record<RatedDimension, number>;
  /** Posterior variance per dimension. */
  v: Record<RatedDimension, number>;
  theta: number;
  thetaVar: number;
}

function posteriorCore(p: Product, pop: Population, reviewCount: number): PosteriorCore {
  const groupKey = peerGroupKey(p);
  const parentKey = String(parentGroupKey(p));
  const group = pop.groups.get(groupKey);
  const parent = pop.parents.get(parentKey);
  const rawRow = pop.raw.get(p.id) ?? rawDimensions(computeAdvancedRating(p));
  const nG = group ? group.sorted.reliability.length : 0;

  const m = {} as Record<RatedDimension, number>;
  const v = {} as Record<RatedDimension, number>;

  for (const dim of RATED_DIMENSIONS) {
    const x = rawRow[dim];
    const fG = group && nG > 1 ? looCdf(group.sorted[dim], x) : 0.5;
    const fP = parent ? looCdf(parent.sorted[dim], x) : 0.5;
    // Sparse peer groups borrow strength from the parent category.
    const blended = (nG * fG + KAPPA * fP) / (nG + KAPPA);
    const z = normInv(clamp(blended, 0.005, 0.995));

    const { precision } = evidenceProfile(p, dim, reviewCount);
    // Prior N(0,1) in peer-normalised units → closed-form posterior.
    const post = 1 / (1 + precision);
    m[dim] = precision * z * post;
    v[dim] = post;
  }

  // Composite with correlated-signal covariance, shrunk toward diagonal.
  let theta = 0;
  for (const dim of RATED_DIMENSIONS) theta += OVERALL_WEIGHTS[dim] * m[dim];

  let quad = 0;
  for (const a of RATED_DIMENSIONS) {
    for (const b of RATED_DIMENSIONS) {
      const cov =
        a === b ? v[a] : (1 - COV_SHRINK) * SIGNAL_RHO * Math.sqrt(v[a] * v[b]);
      quad += OVERALL_WEIGHTS[a] * OVERALL_WEIGHTS[b] * cov;
    }
  }
  const thetaVar = quad + MODEL_SIGMA * MODEL_SIGMA;
  return { m, v, theta, thetaVar };
}

// ─── Public API ────────────────────────────────────────────────────
export interface CalibratedDimension {
  /** 0-100 display score (peer median anchored at 70). */
  score: number;
  /** 90% credible interval in display points. */
  low: number;
  high: number;
  /** Percentile within the product's peer group, 0-100. */
  percentile: number;
  /** P(true score within ±3 points of the shown score), 0-1. */
  confidence: number;
  confidenceLabel: "high" | "medium" | "low";
  evidence: EvidenceProfile;
  /** Raw pre-calibration engine value, for debugging / admin. */
  raw: number;
}

export interface CalibratedRating {
  productId: string;
  modelVersion: string;
  peerGroup: string;
  peerGroupSize: number;
  dimensions: Record<RatedDimension, CalibratedDimension>;
  overall: number;
  overallLow: number;
  overallHigh: number;
  overallPercentile: number;
  overallConfidence: number;
  overallConfidenceLabel: "high" | "medium" | "low";
  /** Standardised residual of θ against log-price. >0 = punches above price. */
  valueResidual: number;
  /** 0-100 rendering of the value residual. */
  valueScore: number;
  badges: string[];
  /** Internal: latent mean/variance, used by headToHead(). */
  theta: number;
  thetaVar: number;
}

const toDisplay = (z: number) => clamp(DISPLAY_CENTER + DISPLAY_SLOPE * z, 0, 100);

function confidenceLabelFor(c: number): "high" | "medium" | "low" {
  return c >= 0.75 ? "high" : c >= 0.5 ? "medium" : "low";
}

function percentileIn(sorted: Float64Array | undefined, x: number): number {
  if (!sorted || sorted.length < 2) return 50;
  return 100 * looCdf(sorted, x);
}

const cache = new Map<string, CalibratedRating>();

/**
 * The single entry point the comparison UI should use for the four
 * displayed rating dimensions.
 */
export function computeCalibratedRating(p: Product, reviewCount = 0): CalibratedRating {
  const key = `${p.id}|${reviewCount}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const pop = population();
  const core = posteriorCore(p, pop, reviewCount);
  const groupKey = peerGroupKey(p);
  const group = pop.groups.get(groupKey);
  const groupSize = group ? group.sorted.reliability.length : 0;
  const rawRow = pop.raw.get(p.id) ?? rawDimensions(computeAdvancedRating(p));

  const badgeSet = new Set<string>();
  const dimensions = {} as Record<RatedDimension, CalibratedDimension>;
  for (const dim of RATED_DIMENSIONS) {
    const sd = Math.sqrt(core.v[dim] + MODEL_SIGMA * MODEL_SIGMA);
    const score = toDisplay(core.m[dim]);
    const ev = evidenceProfile(p, dim, reviewCount);
    ev.badges.forEach((b) => badgeSet.add(b));
    const conf = 2 * normCdf(DELTA_POINTS / (DISPLAY_SLOPE * sd)) - 1;
    dimensions[dim] = {
      score: Math.round(score * 10) / 10,
      low: Math.round(toDisplay(core.m[dim] - 1.645 * sd) * 10) / 10,
      high: Math.round(toDisplay(core.m[dim] + 1.645 * sd) * 10) / 10,
      percentile: Math.round(percentileIn(group?.sorted[dim], rawRow[dim]) * 10) / 10,
      confidence: conf,
      confidenceLabel: confidenceLabelFor(conf),
      evidence: ev,
      raw: Math.round(rawRow[dim] * 10) / 10,
    };
  }

  const sdTheta = Math.sqrt(core.thetaVar);
  const overallConfidence = 2 * normCdf(DELTA_POINTS / (DISPLAY_SLOPE * sdTheta)) - 1;

  // Value: how far above/below the price-predicted quality this product sits.
  const fit = pop.priceFit.get(String(p.category));
  let valueResidual = 0;
  if (fit && p.price > 0) {
    const predicted = fit.intercept + fit.slope * Math.log(p.price);
    valueResidual = (core.theta - predicted) / (fit.residSd || 1);
  }

  const result: CalibratedRating = {
    productId: p.id,
    modelVersion: RATINGS_MODEL_VERSION,
    peerGroup: groupKey,
    peerGroupSize: groupSize,
    dimensions,
    overall: Math.round(toDisplay(core.theta) * 10) / 10,
    overallLow: Math.round(toDisplay(core.theta - 1.645 * sdTheta) * 10) / 10,
    overallHigh: Math.round(toDisplay(core.theta + 1.645 * sdTheta) * 10) / 10,
    overallPercentile: Math.round(percentileIn(pop.thetaByGroup.get(groupKey), core.theta) * 10) / 10,
    overallConfidence,
    overallConfidenceLabel: confidenceLabelFor(overallConfidence),
    valueResidual: Math.round(valueResidual * 100) / 100,
    valueScore: Math.round(clamp(50 + 16 * valueResidual, 0, 100) * 10) / 10,
    badges: [...badgeSet],
    theta: core.theta,
    thetaVar: core.thetaVar,
  };
  cache.set(key, result);
  return result;
}

export interface HeadToHead {
  probability: number;      // P(A is genuinely better than B)
  winner: "a" | "b" | "tie";
  /** True only when the posterior separation clears the 90% threshold. */
  decisive: boolean;
  margin: number;           // display-point difference of the means
  verdict: string;
}

/**
 * Analytic P(θa > θb) from two independent posteriors. Beats ranking on
 * point estimates: a 0.4-point lead with wide intervals is honestly
 * reported as "too close to call".
 */
export function headToHead(a: Product, b: Product, reviewsA = 0, reviewsB = 0): HeadToHead {
  const ra = computeCalibratedRating(a, reviewsA);
  const rb = computeCalibratedRating(b, reviewsB);
  const diff = ra.theta - rb.theta;
  const sd = Math.sqrt(ra.thetaVar + rb.thetaVar);
  const probability = sd > 0 ? normCdf(diff / sd) : 0.5;
  const decisive = probability >= 0.9 || probability <= 0.1;
  const margin = Math.round((ra.overall - rb.overall) * 10) / 10;
  const winner: HeadToHead["winner"] = !decisive ? "tie" : probability > 0.5 ? "a" : "b";
  const pct = Math.round(Math.max(probability, 1 - probability) * 100);
  const verdict = decisive
    ? `${pct}% confident the ${winner === "a" ? a.brand + " " + a.model : b.brand + " " + b.model} is the better product overall.`
    : `Too close to call — only ${pct}% separation. Pick on use case, price, or fitment.`;
  return { probability, winner, decisive, margin, verdict };
}

/** Convenience for cards/lists: the calibrated 0-100 overall. */
export function calibratedOverall(p: Product, reviewCount = 0): number {
  return computeCalibratedRating(p, reviewCount).overall;
}

/**
 * Drop memoised calibrated ratings. Pass a productId to evict just that
 * product (e.g. its live review row was refreshed), or omit it to clear
 * everything after a catalog / model input change.
 */
export function invalidateCalibratedRatings(productId?: string) {
  if (!productId) {
    cache.clear();
    return;
  }
  for (const key of [...cache.keys()]) {
    if (key.startsWith(`${productId}|`)) cache.delete(key);
  }
}

/** Number of memoised rating entries — used by the admin recalibration panel. */
export function calibratedCacheSize(): number {
  return cache.size;
}

